import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ThumbsUp, ThumbsDown, Copy, Pin, Check, ChevronDown, ChevronUp } from "lucide-react";
import type { SourceDocument } from "@/components/types";
import { useState } from "react";

const SourceDocumentCard = ({
  sourceDocumentData,
  isNudged = false,
}: {
  sourceDocumentData: SourceDocument;
  isNudged?: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Check if we have a quote to show initially
  const hasQuote = sourceDocumentData.metadata.isAnswerRef && sourceDocumentData.metadata.quote;

  // Function to highlight quote in full content
  const highlightQuoteInContent = (content: string, quote: string) => {
    if (!quote) return content;

    // Split quote by "..." and find the parts in content
    const quoteParts = quote.split("...").filter((part) => part.trim().length > 0);

    let highlightedContent = content;
    quoteParts.forEach((part) => {
      const trimmedPart = part.trim();
      if (trimmedPart) {
        // Create a regex that's case-insensitive and handles whitespace variations
        const regex = new RegExp(trimmedPart.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
        highlightedContent = highlightedContent.replace(
          regex,
          (match) => `<mark class="bg-yellow-200 px-1 rounded">${match}</mark>`
        );
      }
    });

    return highlightedContent;
  };

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleUpvote = () => {
    console.log("Upvote clicked");
  };

  const handleDownvote = () => {
    console.log("Downvote clicked");
  };

  const handleCopy = () => {
    console.log("Copy clicked");
  };

  const handlePin = () => {
    console.log("Pin clicked");
  };

  return (
    <Card
      className={`rounded-lg shadow-lg border-0 transition-transform duration-300 ${isNudged ? "scale-103 shadow-2xl" : ""}`}
      data-document-id={sourceDocumentData._id}
      dir="rtl"
    >
      <CardContent className="p-6 max-md:p-4">
        {/* First row: Document ID, name, and status badge */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            {/* <span className="text-indigo-900 font-semibold text-lg max-md:text-base">[{sourceDocumentData._id.slice(-4)}]</span> */}
            <span className="text-indigo-900 font-semibold text-lg max-md:text-base">
              {sourceDocumentData.metadata.case_title || sourceDocumentData.metadata.source}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {sourceDocumentData.metadata.isAnswerRef && (
              <Badge className="bg-indigo-500 text-white rounded-lg px-3 py-1 flex items-center gap-1">
                <Pin className="w-3 h-3" />
                סימוכין
              </Badge>
            )}
            <Badge className="bg-green-500 text-white rounded-lg px-3 py-1 flex items-center gap-1">
              <Check className="w-3 h-3" />
              מאומת
            </Badge>
          </div>
        </div>

        <div className="mb-4 text-right">
          <div className="flex items-center justify-start gap-3 text-slate-800">
            <span className="font-medium">
              {Array.isArray(sourceDocumentData.metadata.parties?.plaintiff)
                ? sourceDocumentData.metadata.parties.plaintiff.join(", ")
                : sourceDocumentData.metadata.parties?.plaintiff || "לא זמין"}
            </span>
            <span className="text-slate-500 font-medium">נגד: </span>
            <span className="font-medium">
              {Array.isArray(sourceDocumentData.metadata.parties?.defendant)
                ? sourceDocumentData.metadata.parties.defendant.join(", ")
                : sourceDocumentData.metadata.parties?.defendant || "לא זמין"}
            </span>
          </div>
        </div>

        <div className="mb-4">
          <div className="text-right">
            <span className="text-slate-600 font-medium text-sm">רקע:</span>
            <p className="text-slate-800 text-sm mt-1 leading-relaxed">{sourceDocumentData.metadata.background || "לא זמין"}</p>
          </div>
        </div>

        {/* Second row: Judges names */}
        <div className="mb-4">
          <span className="text-slate-800 text-base max-sm:text-sm">
            שמות השופטים:{" "}
            {Array.isArray(sourceDocumentData.metadata.judges_or_panel)
              ? sourceDocumentData.metadata.judges_or_panel.join(", ")
              : sourceDocumentData.metadata.judges_or_panel || "לא זמין"}
          </span>
        </div>

        {/* Quote section - always show if quote exists */}
        {hasQuote && (
          <div className="mb-4 p-4 bg-indigo-50 border-r-4 border-indigo-400 rounded-lg">
            <div className="flex items-start gap-2 mb-2">
              <span className="text-indigo-600 font-semibold text-sm">ציטוט רלוונטי:</span>
            </div>
            <p className="text-slate-800 text-sm leading-relaxed italic">"{sourceDocumentData.metadata.quote}"</p>
          </div>
        )}

        {/* Third row: Content */}
        <div className="mb-6">
          <div className="text-right">
            <span className="text-slate-600 font-medium text-sm">
              <button
                onClick={handleToggleExpanded}
                className="flex items-center gap-1 text-indigo-500 text-sm mt-3 hover:text-indigo-600 transition-colors group"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="w-4 h-4 group-hover:translate-y-[1px] transition-transform" />
                    <span>הסתר תוכן המסמך</span>
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 group-hover:translate-y-[-1px] transition-transform" />
                    <span>הצג תוכן המסמך</span>
                  </>
                )}
              </button>
            </span>
            <div className="mt-2">
              {isExpanded && (
                // Show full content with highlighted quote (if quote exists)
                <div
                  className="text-slate-800 text-base max-sm:text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: hasQuote
                      ? highlightQuoteInContent(sourceDocumentData.page_content, sourceDocumentData.metadata.quote || "")
                      : sourceDocumentData.page_content,
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Fourth row: Action buttons */}
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleUpvote}
            className="p-2 h-auto text-slate-400 hover:text-slate-600 hover:bg-slate-100"
          >
            <ThumbsUp className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDownvote}
            className="p-2 h-auto text-slate-400 hover:text-slate-600 hover:bg-slate-100"
          >
            <ThumbsDown className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            className="p-2 h-auto text-slate-400 hover:text-slate-600 hover:bg-slate-100"
          >
            <Copy className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePin}
            className="p-2 h-auto text-slate-400 hover:text-slate-600 hover:bg-slate-100"
          >
            <Pin className="w-4 h-4" />
          </Button>
        </div>

        {/* Firth row: Parties */}

        {/* footer row: Metadata and pages */}
        <div className="flex items-center justify-between text-slate-600 text-sm">
          <div className="flex items-center gap-2">
            <span>סוג: {sourceDocumentData.metadata.document_type || "לורם"}</span>
            <span>|</span>
            <span>תאריך: {sourceDocumentData.metadata.date_of_decision || "14/06/25"}</span>
            <span>|</span>
            <span>מקום: {sourceDocumentData.metadata.court_district || "לורם"}</span>
          </div>
          <div className="flex items-center gap-1">
            <span>עמוד:</span>
            <span>{sourceDocumentData.metadata.total_chunks || "-"}</span>
            <span>/</span>
            <span>{sourceDocumentData.metadata.chunk_number || "-"}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SourceDocumentCard;
